import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Card, CardContent,
  Stepper, Step, StepLabel, Alert, CircularProgress,
} from '@mui/material';
import RoleSelector from '../../components/profile/RoleSelector';
import EventSelector from '../../components/profile/EventSelector';
import KeywordInput from '../../components/profile/KeywordInput';
import BehaviorQuestion from '../../components/profile/BehaviorQuestion';
import DualCareerForm from '../../components/profile/DualCareerForm';
import { profileService } from '../../services/profileService';

const steps = ['競技役割', '経験イベント', 'キーワード', '行動特性', 'デュアルキャリア'];

const ProfileCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentResultId = Number(searchParams.get('resultId')) || 0;

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const questionRef = useRef<HTMLDivElement>(null);

  // ステップ変更時に質問部分までスクロール
  useEffect(() => {
    questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeStep]);

  // フォーム状態
  const [roles, setRoles] = useState<string[]>([]);
  const [sportEvents, setSportEvents] = useState<string[]>([]);
  const [nonSportEvents, setNonSportEvents] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>(['', '', '', '', '']);
  const [behavior, setBehavior] = useState({ hardExperience: '', overcomingMethod: '', teamRole: '' });
  const [dualCareer, setDualCareer] = useState({
    practice: '', competition: '', competitionPriority: '',
    workStyle: '', balanceOrientation: '', nonNegotiable: '',
  });

  const canNext = () => {
    switch (activeStep) {
      case 0: return roles.length > 0;
      case 1: return sportEvents.length > 0 || nonSportEvents.length > 0;
      case 2: return keywords.filter((k) => k.trim()).length >= 1;
      case 3: return behavior.hardExperience && behavior.overcomingMethod && behavior.teamRole;
      case 4: return dualCareer.practice && dualCareer.competition && dualCareer.competitionPriority
        && dualCareer.workStyle && dualCareer.balanceOrientation && dualCareer.nonNegotiable;
      default: return false;
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      // 回答保存
      await profileService.saveAnswers({
        assessmentResultId: assessmentResultId || undefined,
        roles,
        events: { sports: sportEvents, nonSports: nonSportEvents },
        keywords: keywords.filter((k) => k.trim()),
        behavior,
        dualCareer,
      });

      // プロフィール生成
      await profileService.generate(assessmentResultId || undefined);

      navigate('/profile/view');
    } catch (err: any) {
      setError(err.response?.data?.message || 'プロフィール生成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0: return <RoleSelector selected={roles} onChange={setRoles} />;
      case 1: return <EventSelector selectedSports={sportEvents} selectedNonSports={nonSportEvents} onChange={(s, n) => { setSportEvents(s); setNonSportEvents(n); }} />;
      case 2: return <KeywordInput keywords={keywords} onChange={setKeywords} />;
      case 3: return <BehaviorQuestion values={behavior} onChange={setBehavior} />;
      case 4: return <DualCareerForm values={dualCareer} onChange={setDualCareer} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3 }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            キャリアプロフィール作成
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            追加設問に回答して、あなたのキャリアプロフィールを生成します
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Card ref={questionRef} sx={{ mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            {renderStep()}
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            戻る
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canNext()}
            >
              次へ
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canNext() || saving}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {saving ? 'プロフィール生成中...' : 'プロフィールを生成する'}
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ProfileCreatePage;
